import {Component, Inject, OnInit, Optional} from '@angular/core';
import { PolicyDefinitionInput } from "../../../management-api-client/model";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { JsonLdObject, Policy } from '@think-it-labs/edc-connector-client';
import { PolicyPresetType } from '../../models/policy-preset-type';
import { CUSTOM_PRESET } from 'src/modules/app/policy-presets';
import { OBLIGATION_RULE, PERMISSION_RULE, PROHIBITION_RULE } from 'src/modules/app/policy-rule-types';
import { PolicyPresetsService, DateTimeService } from '../../services';

interface RuleItem {
  presetTypeId: string,
  policyRuleTypeId: string,
  odrlJson: JsonLdObject,
  allowedLocation: string,
  allowedPurpose: string,
  fromDateString: string,
  toDateString: string,
}

interface RuleListItem extends RuleItem {
  isShowOdrl: boolean
}

@Component({
  selector: 'app-new-policy-dialog',
  templateUrl: './new-policy-dialog.component.html',
  styleUrls: ['./new-policy-dialog.component.scss']
})
export class NewPolicyDialogComponent implements OnInit {
  editMode: boolean = false;

  policy: Policy = new Policy();
  policyDefinition: PolicyDefinitionInput = {
    "policy": this.policy,
    "@id": ''
  };

  addedRulesList: RuleListItem[] = [];
  
  availablePresetTypes: PolicyPresetType[] = [];
  currentRuleItem: RuleItem | null = null;

  constructor(
    @Inject("POLICY_PRESETS") private policyPresetTypes: PolicyPresetType[],
    @Inject("POLICY_RULE_TYPES") public policyRuleTypes: any[],
    private dialogRef: MatDialogRef<NewPolicyDialogComponent>,
    private policyPresetsService: PolicyPresetsService,
    private dateTimeService: DateTimeService,
    @Optional() @Inject(MAT_DIALOG_DATA) private readonly existingPolicyDefinition: any | null) {
  }

  ngOnInit(): void {
    this.availablePresetTypes = [...this.policyPresetTypes];
    this.currentRuleItem = null;
    if (this.existingPolicyDefinition) {
      this.editMode = true;
      this.prefillFromPolicyDefinition(this.existingPolicyDefinition);
    }
  }

  private prefillFromPolicyDefinition(policyDefinition: any): void {
    this.policyDefinition.id = policyDefinition.id ?? policyDefinition['@id'] ?? '';
    this.policyDefinition['@id'] = this.policyDefinition.id;

    const existingPolicy = policyDefinition.policy ?? {};
    this.policy.assigner = existingPolicy.assigner ?? undefined;
    this.policy.assignee = existingPolicy.assignee ?? undefined;

    // existing rules are shown as raw ODRL items; they can be removed and new ones added
    const ruleGroups = [
      { rules: this.toRuleArray(existingPolicy.permissions ?? existingPolicy.permission), typeId: PERMISSION_RULE.id },
      { rules: this.toRuleArray(existingPolicy.prohibitions ?? existingPolicy.prohibition), typeId: PROHIBITION_RULE.id },
      { rules: this.toRuleArray(existingPolicy.obligations ?? existingPolicy.obligation), typeId: OBLIGATION_RULE.id },
    ];
    this.addedRulesList = ruleGroups.flatMap(group => group.rules.map(rule => ({
      presetTypeId: CUSTOM_PRESET.id,
      policyRuleTypeId: group.typeId,
      odrlJson: rule,
      allowedLocation: '',
      allowedPurpose: '',
      fromDateString: '',
      toDateString: '',
      isShowOdrl: false,
    })));
  }

  private toRuleArray(value: any): any[] {
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  }

  createItem() {
    this.currentRuleItem = {
      presetTypeId: this.availablePresetTypes[0].id,
      policyRuleTypeId: this.policyRuleTypes[0].id,
      odrlJson: JSON.parse(this.availablePresetTypes[0].odrlJsonString),
      allowedLocation: '',
      allowedPurpose: '',
      fromDateString: '',
      toDateString: '',
    };

    console.log(this.currentRuleItem);
  }

  onPolicyRuleTypeIdChange(newType: string) {
    if (this.currentRuleItem == null) {
      return;
    }

    if (newType == PERMISSION_RULE.id) {
      this.availablePresetTypes = [...this.policyPresetTypes];
    }
    else {
      this.availablePresetTypes = [CUSTOM_PRESET];
    }
    this.createItem();
    this.currentRuleItem.policyRuleTypeId = newType;
  }

  addItem() {
    if (this.currentRuleItem == null) {
      return;
    }

    console.log(this.currentRuleItem);
    if (this.currentRuleItem.presetTypeId != CUSTOM_PRESET.id) {
      this.policyPresetsService.fillOdrlTemplate(this.currentRuleItem);
    }
    this.addedRulesList.push({ ...this.currentRuleItem, isShowOdrl: false });
    this.resetCurrentItem();
  }

  cancelItem() {
    this.resetCurrentItem();
  }

  removeItem(index: number) {
    this.addedRulesList.splice(index, 1);
  }

  onJsonLdUpdated(updatedJson: any) {
    if (this.currentRuleItem == null) {
      return;
    }
    this.currentRuleItem.odrlJson = updatedJson;
  }

  toggleIsShowOdrl(ruleListItem: any) {
    ruleListItem.isShowOdrl = !ruleListItem.isShowOdrl;
  }

  setNowFromDate() {
    if (this.currentRuleItem == null) {
      return;
    }
    this.currentRuleItem.fromDateString = this.dateTimeService.getCurrentLocalTimeString();
  }

  setNowToDate() {
    if (this.currentRuleItem == null) {
      return;
    }
    this.currentRuleItem.toDateString = this.dateTimeService.getCurrentLocalTimeString();
  }

  onPresetTypeChange(newType: string) {
    if (this.currentRuleItem == null) {
      return;
    }

    if (newType == CUSTOM_PRESET.id) {
      this.currentRuleItem.odrlJson = JSON.parse(CUSTOM_PRESET.odrlJsonString);
    }
    else {
      this.currentRuleItem.odrlJson = this.findFirstOdrlAndGetJson(newType);
    }
  }

  resetCurrentItem() {
    if (this.currentRuleItem == null) {
      return;
    }

    this.currentRuleItem = null;
    this.availablePresetTypes = [...this.policyPresetTypes];
  }

  onSave() {
    const rulesDictionary: any = {};
    rulesDictionary[PERMISSION_RULE.id] = [];
    rulesDictionary[PROHIBITION_RULE.id] = [];
    rulesDictionary[OBLIGATION_RULE.id] = [];

    this.addedRulesList.forEach(rule => {
      const rulesList = rulesDictionary[rule.policyRuleTypeId];
      rulesList.push(rule.odrlJson);
    });

    this.policy.permission = rulesDictionary[PERMISSION_RULE.id];
    this.policy.prohibition = rulesDictionary[PROHIBITION_RULE.id];
    this.policy.obligation = rulesDictionary[OBLIGATION_RULE.id];

    const USE_PERMISSION = [{ action: 'use' }];

    // defaults are only applied on creation; saving an edited policy must not add rules it never had
    if (!this.editMode) {
      if (this.policy.permission.length == 0) {
        this.policy.permission.push(...USE_PERMISSION);
      }

      if (this.policy.prohibition.length == 0) {
        this.policy.prohibition.push(...USE_PERMISSION);
      }
    }

    this.policy["@type"]="Set";
    this.policy["@context"]="http://www.w3.org/ns/odrl.jsonld";

    this.dialogRef.close({
      policy : this.policyDefinition.policy,
      '@id': this.policyDefinition.id
    })
  }

  private findFirstOdrlAndGetJson(id: string) {
    return JSON.parse(this.policyPresetTypes.find(x => x.id == id)!.odrlJsonString);
  }
}
